import React, { useState } from 'react';

import PhotoEditorServiceModal from 'components/modals/photo_editor_service_modal';
import PhotoBulkActions from 'components/photos/bulk_actions';
import PhotoCard from 'components/photos/photo_card';
import PhotoRow from 'components/photos/photo_row';
import UploadButton from 'components/photos/upload_button';
import { reorderableList } from 'helpers/react_dnd/reorderable_list';
import { NEW_PHOTO_ATTACHMENT_ID_PREFIX } from 'helpers/photos/constants';
import { openPhotoPreview } from 'helpers/photos/photo_preview';
import { Alert } from 'react-bootstrap-5';

// TODO: clean this type up
export type PhotoAttachment = {
  url?: string,
  fullUrl?: string,
  id: number | string,
  photoId?: number,
  syndicate?: boolean,
  processing?: boolean,
  deleted?: boolean,
  thumbnailUrl?: string,
  originalUploadId?: string | number,
  uploadId?: string | number,
  uploaded?: boolean,
  dataContentType?: string,
  sortOrder?: number,
  sidekiqJobId?: string | null,
  sidekiqStatus?: string,
  attachmentType?: string,
  description?: string | null,
  digitallyAltered?: boolean,
  originalUrl?: string
}

export type Props = {
  bulkDownloadPhotosLink: string,
  classSlug: string,
  defaultPhotoOption: string,
  digitallyAlteredEnabled?: boolean,
  downloadPhotoLink?: string,
  maxPhotos: number,
  name?: string,
  photoAttachments: Array<PhotoAttachment>,
  photoEditorUrl: string,
  photoTypeOptions: Array<string>,
  showEdit: boolean,
  showLimit: boolean,
  syndication: boolean,
  userAdmin?: boolean,
  onPhotoChange: (photos: Array<PhotoAttachment>) => void,
  osSection?: (actions: React.ReactNode, content: React.ReactNode, hasPhotos: boolean) => React.ReactNode
}

function PhotoForm({
  bulkDownloadPhotosLink,
  classSlug,
  defaultPhotoOption,
  digitallyAlteredEnabled,
  downloadPhotoLink,
  maxPhotos,
  name,
  photoAttachments,
  photoEditorUrl,
  photoTypeOptions,
  showEdit = true,
  showLimit = false,
  syndication,
  userAdmin,
  onPhotoChange,
  osSection
}: Props) {
  const [photoToEditId, setPhotoToEditId] = useState<number | string>();
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<(number | string)[]>([]);
  const photoToEdit = photoAttachments.find(photo => photo.id === photoToEditId);

  function showSyndicationPhotoWarning() {
    return !visiblePhotoAttachments().some(pa => pa.syndicate);
  }

  function rowKey(photoAttachment) {
    const { id, uploadId, photoId, copy } = photoAttachment;

    if (id) return id;
    if (copy) return `c${photoId}`;
    if (uploadId) return `u${uploadId}`;
  }

  function allowUpload() {
    if (maxPhotos && visiblePhotoAttachments().length >= maxPhotos) {
      alert(`Cannot exceed ${maxPhotos} photos`);
      return false;
    } else {
      return true;
    }
  }

  function handlePhotoAttachmentsUpdate(updatedPhotoAttachments: Array<PhotoAttachment>) {
    onPhotoChange(updatedPhotoAttachments);
  }

  // Applies one or more attribute changes to a single photo in a single update. Callers pass an object of
  // changes ({ syndicate: true }, { digitallyAltered: true, originalUrl }, ...). Because photoAttachments is
  // a prop, fields that change together must be set in one call: two back-to-back calls would each read the
  // same stale array and the second would clobber the first.
  function handleAttributeChange(index: number, changes: Partial<PhotoAttachment>) {
    const updatedPhotoAttachments = [...photoAttachments];
    updatedPhotoAttachments[index] = { ...updatedPhotoAttachments[index], ...changes };
    handlePhotoAttachmentsUpdate(updatedPhotoAttachments);
  }

  function handleBulkAttributeChange(attribute: string, value) {
    const selected = selectedPhotoAttachments();
    const updatedPhotoAttachments = photoAttachments.map((photoAttachment) => {
      if (selected.includes(photoAttachment)) {
        return { ...photoAttachment, [attribute]: value };
      }

      return photoAttachment;
    });
    handlePhotoAttachmentsUpdate(updatedPhotoAttachments);
  }

  function handleUpload(data) {
    const newPhotoAttachment: PhotoAttachment = {
      url: data.url,
      thumbnailUrl: data.url,
      originalUploadId: data.id,
      uploadId: data.id, //unnecessary?
      uploaded: true,
      dataContentType: data.file_type,
      id: `${NEW_PHOTO_ATTACHMENT_ID_PREFIX}${data.id}`,
    };

    const updatedPhotoAttachments = [...photoAttachments, newPhotoAttachment];
    handlePhotoAttachmentsUpdate(updatedPhotoAttachments);
  }

  function handlePhotoReorder(dragIndex: number, hoverIndex: number) {
    const photoAttachmentsShallow = [...photoAttachments];
    const dragPhoto = photoAttachmentsShallow[dragIndex];
    // in rare cases, dragPhoto can return undefined.
    // we are unsure on why it happens, but it seems like a very rare edge case.
    if (!dragPhoto) {
      handlePhotoAttachmentsUpdate(photoAttachmentsShallow);
      return;
    }

    photoAttachmentsShallow.splice(dragIndex, 1);
    photoAttachmentsShallow.splice(hoverIndex, 0, dragPhoto);
    const updatedPhotoAttachments = photoAttachmentsShallow.map((photoAttachment, index) => (
      { ...photoAttachment, sortOrder: index }
    ));
    handlePhotoAttachmentsUpdate(updatedPhotoAttachments);
  }

  function handleCloseEditModal() {
    setPhotoToEditId(undefined);
  }

  function handleOpenEditModal(photoForEdit: PhotoAttachment) {
    setPhotoToEditId(photoForEdit.id);
  }

  function handlePhotoEditSave(photoUpdate) {
    const updatedPhotoAttachments = photoAttachments.map((photoAttachment) => {
      if (photoAttachment.id === photoToEditId) {
        return { ...photoAttachment, uploadId: photoUpdate.id, url: photoUpdate.url, thumbnailUrl: photoUpdate.url };
      }

      return photoAttachment;
    });

    handlePhotoAttachmentsUpdate(updatedPhotoAttachments);
    setPhotoToEditId(undefined);
  }

  function renderPhotoEditor() {
    if (!photoEditorUrl) return;

    return (
      <PhotoEditorServiceModal

        imageUrl={photoToEdit ? photoToEdit.url : ''}
        photoEditorUrl={photoEditorUrl}
        show={!!photoToEdit}
        onHide={handleCloseEditModal}
        onSave={handlePhotoEditSave}
      />
    );
  }

  function renderNoSyndicatablePhotosWarning() {
    const showWarning = syndication && showSyndicationPhotoWarning();
    if (!showWarning) return null;

    return <Alert variant="warning">
      <div>
        Your listing photos will not be included on third party listing
        sites or Buildout Buyer Network unless they are approved.
      </div>

      <strong> Please review and check the appropriate boxes below for any approved photos.</strong>
    </Alert>;
  }

  function handleBulkCheckboxClicked(id, isChecked) {
    if (isChecked) {
      setSelectedPhotoIds(previousSelected => previousSelected.filter(selectedId => selectedId !== id));
    } else {
      setSelectedPhotoIds(previousSelected => ([...previousSelected, id]));
    }
  }

  function visiblePhotoAttachments() {
    return photoAttachments.filter(photoAttachment => !photoAttachment.deleted);
  }

  function selectedPhotoAttachments() {
    return visiblePhotoAttachments().filter(photoAttachment => selectedPhotoIds.includes(rowKey(photoAttachment)));
  }

  function handleBulkPreview() {
    selectedPhotoAttachments().forEach(photoAttachment => openPhotoPreview(photoAttachment));
    setSelectedPhotoIds([]);
  }

  function handleBulkDownload() {
    setTimeout(() => setSelectedPhotoIds([]), 50);
  }

  function allPhotosSelected() {
    const visible = visiblePhotoAttachments();
    return visible.length > 0 && visible.every(photoAttachment => selectedPhotoIds.includes(rowKey(photoAttachment)));
  }

  function handleToggleAll() {
    if (allPhotosSelected()) {
      setSelectedPhotoIds([]);
    } else {
      setSelectedPhotoIds(visiblePhotoAttachments().map(photoAttachment => rowKey(photoAttachment)));
    }
  }

  // A photo added in this session has no saved id yet, so there is nothing for the server to look up.
  function canDownload(photoAttachment: PhotoAttachment) {
    return Boolean(downloadPhotoLink) && !photoAttachment.uploaded;
  }

  // The server sets the filename and the attachment disposition, which a link from here could not.
  // Open rather than navigate: a photo saved moments ago may still be waiting on ReprocessPhotoWorker
  // for its full-size style, and navigating to that error would take the tab's unsaved edits with it.
  function handleDownload(photoAttachment: PhotoAttachment) {
    if (!downloadPhotoLink) return;

    const downloadLink = new URL(downloadPhotoLink);
    downloadLink.searchParams.append('photo_attachment_id', photoAttachment.id.toString());
    window.open(downloadLink.toString(), '_blank');
  }

  function bulkDownloadLinkWithIds() {
    if (!bulkDownloadPhotosLink) return;

    const downloadPhotosLink = new URL(bulkDownloadPhotosLink);

    selectedPhotoAttachments().forEach((photoAttachment) => {
      downloadPhotosLink.searchParams.append('photo_attachment_ids[]', rowKey(photoAttachment).toString());
    });

    return downloadPhotosLink.toString();
  }

  const primaryIndex = photoAttachments.findIndex(photo => !photo.deleted);
  const hasPhotos = primaryIndex !== -1;

  const bulkActions = (
    <PhotoBulkActions
      allSelected={allPhotosSelected()}
      bulkDownloadLink={bulkDownloadLinkWithIds()}
      disabled={selectedPhotoAttachments().length === 0}
      osHeader={!!osSection}
      showLimit={showLimit}
      onBulkAttributeChange={handleBulkAttributeChange}
      onBulkDelete={() => handleBulkAttributeChange('deleted', true)}
      onBulkDownload={handleBulkDownload}
      onBulkPreview={handleBulkPreview}
      onSelectAll={handleToggleAll}
    />
  );

  const photoGrid = (
    <div className="photo-grid">
      {!osSection && (
        <PhotoCard
          allowUpload={allowUpload}
          onUpload={handleUpload}
        />
      )}

      {photoAttachments.map((photoAttachment, index) => {
        const uniqueId = rowKey(photoAttachment);
        const isChecked = selectedPhotoIds.includes(uniqueId);
        return (
          <PhotoRow
            classSlug={classSlug}
            defaultPhotoOption={defaultPhotoOption}
            digitallyAlteredEnabled={digitallyAlteredEnabled}
            index={index}
            isChecked={isChecked}
            isPrimary={index === primaryIndex}
            key={uniqueId}
            mediaUiUpdate={true}
            osCard={!!osSection}
            photoAttachment={photoAttachment}
            photoEditorUrl={photoEditorUrl}
            photoTypeOptions={photoTypeOptions}
            showDelete={!osSection}
            showEdit={showEdit}
            targetGroup={name}
            userAdmin={userAdmin}
            onAttributeChange={handleAttributeChange}
            onCheckboxClicked={() => handleBulkCheckboxClicked(uniqueId, isChecked)}
            onDownload={canDownload(photoAttachment) ? handleDownload : undefined}
            onEdit={handleOpenEditModal}
            onPreview={openPhotoPreview}
            onReorder={handlePhotoReorder}
          />
        );
      })
      }
    </div>
  );

  if (osSection) {
    return (
      <React.Fragment>
        {osSection(
          <div className="d-flex align-items-center gap-3">
            {hasPhotos && bulkActions}
            <UploadButton allowUpload={allowUpload} onUpload={handleUpload} />
          </div>,
          <div className="d-flex flex-column gap-4">
            {renderNoSyndicatablePhotosWarning()}
            {photoGrid}
          </div>,
          hasPhotos
        )}
        {renderPhotoEditor()}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="d-flex flex-column gap-4">
        {bulkActions}
        {renderNoSyndicatablePhotosWarning()}
        {photoGrid}
        {renderPhotoEditor()}
      </div>
    </React.Fragment>
  );
}

export default reorderableList(PhotoForm);
